<script setup>
import { ref, computed, onMounted, getCurrentInstance, defineEmits, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePageLoadingStore } from "../../../../../stores/pageLoading";
import KSASecondaryButton from "../../../../../components/ui-components/button/KSASecondaryButton.vue";
import KSAPrimaryButton from "../../../../../components/ui-components/button/KSAPrimaryButton.vue";
import JobFairJobNoticeDetailModal from '../../../../user/components/modal/jobFair/JobFairJobNoticeDetailModal.vue';
import KSATextButton from "../../../../../components/ui-components/button/KSATextButton.vue";
import adminJobFairApplicantService from '../../../services/jobfair/adminJobFairApplicantService';
import jobFairUserService from "../../../../user/services/jobFair/jobFairUserService";
import mypageJobFairApplicantService from "../../../../user/services/mypage/jobFair/mypageJobFairApplicantService";
import JobFairResumeDetailModal from "../../../../user/components/modal/jobFair/JobFairResumeDetailModal.vue";

const route = useRoute()
const router = useRouter()
const loadingStore = usePageLoadingStore()
const { $pageListBoardNo, $toast, alert, confirm } = getCurrentInstance().appContext.config.globalProperties;

const props = defineProps({
  resumeId: {
    type: [String, Number],
    default: null,
  },
  userId: {
    type: [String, Number],
    default: null,
  },
  fileYn: {
    type: String,
    default: '',
  },
});

const currentPage = ref(1);
const pageSize = ref(10);
const totalPages = ref(1);
const totalCount = ref(0);
const list = ref([]);

const jobNoticeModal = ref(null);
const jobFairResumeModal = ref(null);


// 입사지원 목록
const retrieveList = () => {
  let paramObj = {
    resumeId: props.resumeId,
    userId: props.userId,
    pageNumber: currentPage.value || 0,
    size: pageSize.value || 10,
  }

  loadingStore.loading = true;
  mypageJobFairApplicantService.retrieveApplicantListPaging(paramObj)
    .then( (res) => {
      if(res.data.code.toLowerCase() === 'success') {
        const result = res.data.result;
        // console.log(result);
        totalPages.value = result.totalPages;
        totalCount.value = result.totalElements;
        list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
      }
      loadingStore.loading = false;
    })
    .catch( (error) =>  {
      loadingStore.loading = false;
    });
}

// 채용공고 보기
const onClickJobNotice = async (jobNoticeSeq, corpId) => {
  await jobNoticeModal.value.show({
    jobNoticeSeq,
    corpId,
  });
}

// 이력서 보기
const onClickMyResume = async () => {
  await jobFairResumeModal.value.show({
    resumeId: props.resumeId,
    showDeleteBtn: true,
  });
}

// 입사지원 상태변경 joinStateGb
const onClickApplyStateGb = async (resumeId, jobNoticeSeq, stateGb) => {
  console.log(stateGb)
  let modalContent, okButtonText;
  if (stateGb === "02") {
    modalContent = ["지원취소 처리하시겠습니까?", "'지원취소'으로 상태가 변경됩니다."];
    okButtonText = "지원취소";
  } else if (stateGb === "03") {
    modalContent = ["서류합격 처리하시겠습니까?", "'서류합격'으로 상태가 변경됩니다."];
    okButtonText = "서류합격";
  } else if (stateGb === "04") {
    modalContent = ["서류불합격 처리하시겠습니까?", "'서류불합격'으로 상태가 변경됩니다."];
    okButtonText = "서류불합격";
  } else if (stateGb === "05") {
    modalContent = ["최종합격 처리하시겠습니까?", "'최종합격'으로 상태가 변경됩니다."];
    okButtonText = "최종합격";
  } else if (stateGb === "06") {
    modalContent = ["최종불합격 처리하시겠습니까?", "'최종불합격'으로 상태가 변경됩니다."];
    okButtonText = "최종불합격";
  }

  const ok = await confirm(modalContent, okButtonText);
  if(ok) {
    const paramObj = {
      resumeId: resumeId,
      jobNoticeSeq: jobNoticeSeq,
      applyStateGb: stateGb,
    };
    mypageJobFairApplicantService.updateApplyStateGb(paramObj)
      .then( (res) => {
        loadingStore.loading = false;
        if(res.data.code.toLowerCase() === 'success') {
          alert("변경하였습니다.");
          retrieveList();
        } else {
          alert("변경을 실패하였습니다.");
        }
      })
      .catch( (res) =>  {
        loadingStore.loading = false;
        alert("변경을 실패하였습니다.");
      });
  }
}

const openDetailInNewTab = (linkName, id, query = {}) => {
  const url = router.resolve({
    name: linkName,
    params: { id },
    query,
  }).href;

  window.open(url, '_blank');
}

const emit = defineEmits(['go-back']);

const goBack = () => {
  emit("go-back");
}

onMounted(() => {
  retrieveList();
})
</script>

<template>
  <div>
    <KSASecondaryButton text="이전" @click="goBack()" />
    <div class="list-top">
      <p>전체 <span>{{ $number(totalCount)}}</span>개</p>
      <div>
        <KSAPrimaryButton v-show="fileYn === 'N'" text="이력서 보기" @click="onClickMyResume()" />
      </div>
    </div>
    <div class="table-form">
      <table class="horizontal">
        <tr>
          <th>NO</th>
          <th>채용상태</th>
          <th>기업명</th>
          <th>채용공고 제목</th>
          <th>마스터</th>
          <th>면접 방식</th>
          <th>채용기간</th>
          <th>입사 지원일</th>
          <th>상태</th>
          <th>상태변경</th>
        </tr>
        <tr v-for="applicant in list">
          <td>{{ applicant.boardNo }}</td>
          <td>{{ $gbNameKR('JOB_NOTICE_STATE_GB', applicant.jobNoticeStateGb) }}</td>
          <td class="clickable blue text-overflow long-overflow">
            <span @click="openDetailInNewTab('CorporationDetail', applicant.corpId, { tab: 3, tab2: 'job-postings' })">{{ applicant.corpName }}</span>
          </td>
          <td class="clickable blue text-overflow long-overflow" @click="onClickJobNotice(applicant.jobNoticeSeq, applicant.corpId)">{{ applicant.jobName }}</td>
          <td class="clickable blue">
            <span @click="openDetailInNewTab('MemberDetail', applicant.userId)">{{ applicant.userName }}</span>
          </td>
          <td>{{ $gbNameKR('INTV_GB', applicant.intvGb) }}</td>
          <td>{{ $yyyymmdd(applicant.recruitStartYmd) }} ~ {{ $yyyymmdd(applicant.recruitEndYmd) }}</td>
          <td>{{ $yyyymmddHHmmss(applicant.createDate) }}</td>
          <td>
            <span v-if="applicant.applyStateGb === '01'">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
            <span v-else-if="applicant.applyStateGb === '03' || applicant.applyStateGb === '05'" class="blue">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
            <span v-else-if="applicant.applyStateGb === '04' || applicant.applyStateGb === '06'" class="red">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
            <span v-else class="gray">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
          </td>
          <td>
            <div v-if="applicant.applyStateGb === '01'">
              <KSATextButton class="clickable black" text="지원취소" @click="onClickApplyStateGb(applicant.resumeId, applicant.jobNoticeSeq, applicant.jobNoticeStateGb)"/>
            </div>
            <div v-else>
              -
            </div>
          </td>
        </tr>
      </table>
      <div class="result-none" v-if="list.length === 0">
        입사지원 내역이 없습니다.
      </div>
    </div>
    <Paginate
      v-if="list.length > 0"
      v-model="currentPage"
      :page-count="totalPages"
      :click-handler="onSelectPage"
    />
  </div>
  <JobFairJobNoticeDetailModal ref="jobNoticeModal" /> <!-- 채용공고 보기 -->
  <JobFairResumeDetailModal ref="jobFairResumeModal" /> <!-- 이력서 보기 -->
</template>

<style scoped>
</style>
